import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar, type SnackbarKey } from 'notistack';
import { Button } from '@mui/material';

const POLL_INTERVAL_MS = 60 * 1000;
const LATER_DELAY_MS = 30 * 60 * 1000;
const SNACKBAR_KEY = 'stale-app-warning';

/** Same URL with a fresh cache-busting `refresh` param, preserving path, query and fragment. */
export function buildReloadUrl(href: string): string {
  const url = new URL(href);
  url.searchParams.set('refresh', String(Date.now()));
  return url.toString();
}

/**
 * Offers a reload when a newer build is deployed. The build stamps a version into
 * <meta name="version"> and a matching /version.txt (see the version-stamp plugin in
 * vite.config.ts); a tab on an older build sees them diverge. Mount once.
 */
export function StaleAppWarning() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [currentVersion, setCurrentVersion] = useState('');
  // A fresh object every poll so the check below re-runs even when the version
  // string is unchanged — that's how the "Later" delay is re-evaluated.
  const [remote, setRemote] = useState({ time: 0, version: '' });
  const delayedUntil = useRef(0);

  useEffect(() => {
    const content = document.querySelector('meta[name="version"]')?.getAttribute('content') || '';
    if (content && content !== '{{version}}') setCurrentVersion(content);
  }, []);

  const fetchRemoteVersion = useCallback(() => {
    // Bare fetch: no auth interceptor, and version.txt is public.
    fetch(`/version.txt?${Date.now()}`)
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((text) => {
        // Ignore a non-numeric body: an SPA index.html served for a missing or
        // misnamed version.txt would never match and would nag through reloads.
        const version = text.trim();
        if (/^\d+$/.test(version)) setRemote({ time: Date.now(), version });
      })
      .catch(() => {}); // no version.txt in dev, and a blip just retries next tick
  }, []);

  useEffect(() => {
    if (!currentVersion) return;
    fetchRemoteVersion();
    const id = setInterval(fetchRemoteVersion, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [currentVersion, fetchRemoteVersion]);

  const reloadPage = useCallback(() => {
    window.location.href = buildReloadUrl(window.location.href);
  }, []);

  useEffect(() => {
    if (!currentVersion || !remote.version || currentVersion === remote.version) return;
    if (Date.now() < delayedUntil.current) return;

    enqueueSnackbar('A new version is available', {
      key: SNACKBAR_KEY,
      variant: 'info',
      persist: true,
      preventDuplicate: true,
      action: (key: SnackbarKey) => (
        <>
          <Button color="inherit" size="small" onClick={reloadPage}>
            Reload
          </Button>
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              delayedUntil.current = Date.now() + LATER_DELAY_MS;
              closeSnackbar(key);
            }}
          >
            Later
          </Button>
        </>
      ),
    });
  }, [currentVersion, remote, enqueueSnackbar, closeSnackbar, reloadPage]);

  return null;
}

export default StaleAppWarning;
